/**
 * Post-insert composition skin — keeps sections added AFTER a Skin from URL
 * composition client-branded.
 *
 * Without this, addSection / swap-variant / addTemplatePage insert raw
 * catalog HTML and immediately reintroduce the SOURCE business's name,
 * links, and imagery into a client-branded project. Every insert path flows
 * through section-manager's createInstance, which calls applyCompositionSkin
 * when state.compositionContext is present.
 *
 * Design constraints:
 *  - runs synchronously inside createInstance (browser) — DOM via DOMParser
 *    (injectable for tests);
 *  - deterministic photo choice WITHOUT store mutation: hash(catalogKey +
 *    group ordinal) picks from clientProfile.heroImages;
 *  - idempotent: re-running on already-skinned HTML produces no diff
 *    (identity pairs no-op, bindings skip equal slots, neutralized links
 *    are skipped);
 *  - CSS urls handled string-level (no css-tree in the browser bundle).
 *
 * Worker-safe module (importable everywhere); the default parseHTML uses
 * DOMParser when present.
 */

import { makeTextPairs, replaceTextWithPairs } from './identity-pairs.js';
import { applyContentBindings } from './content-bindings.js';
import { applyLinkPolicy } from './link-policy.js';
import { ASSET_ROLES, classifyImageRole, isRasterUrl } from './asset-role.js';
import {
  LOGO_SENTINEL_VALUE,
  isSourceDomainUrl,
  normalizeUrlForMatch
} from './source-domain-url.js';
import { normalizeClientInfo } from './client-info.js';

const TEXT_ATTRIBUTES = ['alt', 'aria-label', 'title', 'placeholder'];

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeDomain(value) {
  return clean(value)
    .toLowerCase()
    .replace(/^www\./, '')
    .replace(/\/+$/, '');
}

function defaultParseHTML(html) {
  if (typeof DOMParser === 'undefined') return null;
  return new DOMParser().parseFromString(html, 'text/html');
}

/** Small deterministic string hash for stable photo selection. */
function stringHash(value) {
  let hash = 5381;
  const text = String(value);
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function walkTextNodes(node, visit) {
  if (!node) return;
  if (node.nodeType === 3) {
    visit(node);
    return;
  }
  if (!node.childNodes) return;
  const tag = node.tagName ? node.tagName.toLowerCase() : '';
  if (tag === 'script' || tag === 'style') return;
  for (const child of Array.from(node.childNodes)) walkTextNodes(child, visit);
}

/**
 * SK-6. DELIBERATELY UNCAPPED, unlike `brand-skin.js`'s bundle-wide assigner.
 *
 * Two reasons, and they are both properties of this call site rather than
 * preferences. It runs per INSERTED SECTION, so the failure the bundle cap
 * exists for — one photograph filling twenty slots across a whole site — cannot
 * arise here: the ordinal restarts at zero for every insert and a single section
 * has a handful of photo slots. And the picker is seeded by a content hash so
 * that inserting the same section twice yields the same imagery; a generation
 * ceiling would make the Nth insert of one section disagree with the first,
 * which is exactly the determinism this seed was introduced to buy.
 *
 * SU-8 briefly capped this and reverted: see `brand-skin.js`'s assigner for the
 * measurement. A per-section quota did not reduce repetition at all — the
 * per-page ceiling already did that — and its only measured effect was to leave
 * more of the TEMPLATE business's hotlinked images on the page.
 */
function createHashedPhotoPicker(clientProfile, seed) {
  const heroImages = (Array.isArray(clientProfile?.heroImages) ? clientProfile.heroImages : [])
    .map(clean)
    .filter(Boolean);
  let ordinal = 0;
  return () => {
    if (!heroImages.length) return null;
    const url = heroImages[stringHash(`${seed}:${ordinal}`) % heroImages.length];
    ordinal += 1;
    return url;
  };
}

function markMissingLogo(el, attr) {
  el.setAttribute(attr, LOGO_SENTINEL_VALUE);
  el.setAttribute('data-pb-needs-upload', 'logo');
  if (attr === 'src' && el.hasAttribute('srcset')) el.removeAttribute('srcset');
}

function keepForReview(el, role) {
  el.setAttribute('data-pb-needs-review', role);
}

function preserveSourceAsset(el, role) {
  el.setAttribute('data-pb-source-asset', role);
}

function logoMatcher(sourceProfile, sourceDomain) {
  const candidates = new Set(
    [
      sourceProfile?.logoSrc,
      ...(Array.isArray(sourceProfile?.logoVariants) ? sourceProfile.logoVariants : [])
    ]
      .flatMap((value) => [clean(value), normalizeUrlForMatch(value, sourceDomain)])
      .filter(Boolean)
  );
  return (img, src) =>
    candidates.has(clean(src)) || candidates.has(normalizeUrlForMatch(src, sourceDomain));
}

function flagForeignRasterImages(root, clientProfile) {
  const clientImages = new Set((clientProfile?.heroImages || []).map(clean).filter(Boolean));
  // SU-8. `heroImages` is only the AUTO-SWAP pool — at most 8 of the ~80
  // photographs SU-1 collects. Every other image the client owns arrives
  // through the picker's "From this site" source, and judging those foreign
  // would stamp `data-pb-needs-review="foreign-image"` on the very images a
  // person just deliberately chose. The inventory and its responsive variants
  // are the client's too.
  for (const entry of clientProfile?.siteImages || []) {
    const url = clean(entry?.url);
    if (url) clientImages.add(url);
    for (const variant of entry?.variants || []) {
      const v = clean(variant);
      if (v) clientImages.add(v);
    }
  }
  if (clean(clientProfile?.logoUrl)) clientImages.add(clean(clientProfile.logoUrl));
  for (const img of root.querySelectorAll?.('img[src]') || []) {
    const src = clean(img.getAttribute('src'));
    if (!src || clientImages.has(src) || src.startsWith('pb-asset://') || src.startsWith('data:'))
      continue;
    if (isRasterUrl(src)) keepForReview(img, 'foreign-image');
  }
}

function skinAssets(root, sourceDomain, sourceProfile, clientProfile, nextPhoto) {
  if (!sourceDomain) {
    flagForeignRasterImages(root, clientProfile);
    return;
  }
  const processed = new Set();
  const isLogo = logoMatcher(sourceProfile, sourceDomain);
  const srcsetTouches = (srcset) =>
    String(srcset || '')
      .split(',')
      .some((part) => isSourceDomainUrl(part.trim().split(/\s+/)[0], sourceDomain));

  for (const picture of root.querySelectorAll?.('picture') || []) {
    const img = picture.querySelector('img');
    const sources = Array.from(picture.querySelectorAll('source'));
    const touched =
      (img &&
        (isSourceDomainUrl(img.getAttribute('src'), sourceDomain) ||
          srcsetTouches(img.getAttribute('srcset')))) ||
      sources.some((source) => srcsetTouches(source.getAttribute('srcset')));
    if (!touched) continue;
    for (const node of [img, ...sources]) {
      if (node) processed.add(node);
    }

    if (
      img &&
      !isSourceDomainUrl(img.getAttribute('src'), sourceDomain) &&
      !srcsetTouches(img.getAttribute('srcset'))
    ) {
      for (const source of sources) {
        if (srcsetTouches(source.getAttribute('srcset'))) source.remove();
      }
      continue;
    }

    const role = img ? classifyImageRole(img, { isLogo }) : ASSET_ROLES.PHOTO;
    if (role === ASSET_ROLES.LOGO) {
      for (const source of sources) source.remove();
      if (img) markMissingLogo(img, 'src');
      continue;
    }
    if (role === ASSET_ROLES.PHOTO) {
      const url = nextPhoto();
      if (url) {
        for (const source of sources) {
          source.setAttribute('srcset', url);
          source.removeAttribute('type');
        }
        if (img) {
          img.setAttribute('src', url);
          if (img.hasAttribute('srcset')) img.setAttribute('srcset', url);
        }
        continue;
      }
      keepForReview(picture, ASSET_ROLES.PHOTO);
      continue;
    }
    if (role === ASSET_ROLES.AVATAR) {
      keepForReview(picture, ASSET_ROLES.AVATAR);
      continue;
    }
    preserveSourceAsset(picture, role);
  }

  for (const el of root.querySelectorAll?.('[src]') || []) {
    if (processed.has(el)) continue;
    const src = el.getAttribute('src');
    if (!isSourceDomainUrl(src, sourceDomain)) continue;
    const tag = el.tagName?.toLowerCase() || '';
    if (tag !== 'img') {
      el.removeAttribute('src');
      el.setAttribute('data-pb-needs-review', 'embed');
      continue;
    }
    const role = classifyImageRole(el, { isLogo, src });
    if (role === ASSET_ROLES.LOGO) {
      markMissingLogo(el, 'src');
      continue;
    }
    if (role === ASSET_ROLES.PHOTO) {
      const url = nextPhoto();
      if (url) {
        el.setAttribute('src', url);
        if (el.hasAttribute('srcset')) el.setAttribute('srcset', url);
        continue;
      }
      keepForReview(el, ASSET_ROLES.PHOTO);
      continue;
    }
    if (role === ASSET_ROLES.AVATAR) keepForReview(el, ASSET_ROLES.AVATAR);
    else preserveSourceAsset(el, role);
  }

  for (const el of root.querySelectorAll?.('[srcset]') || []) {
    if (processed.has(el)) continue;
    if (!srcsetTouches(el.getAttribute('srcset'))) continue;
    const role =
      el.tagName?.toLowerCase() === 'img' ? classifyImageRole(el, { isLogo }) : ASSET_ROLES.PHOTO;
    if (role === ASSET_ROLES.LOGO) markMissingLogo(el, 'srcset');
    else if (role === ASSET_ROLES.PHOTO) {
      const url = nextPhoto();
      if (url) el.setAttribute('srcset', url);
      else keepForReview(el, ASSET_ROLES.PHOTO);
    } else if (role === ASSET_ROLES.AVATAR) keepForReview(el, ASSET_ROLES.AVATAR);
    else preserveSourceAsset(el, role);
  }
}

/** CSS background assets are layout decoration and remain source-hosted. */
function skinCssUrls(css) {
  return css || '';
}

function resolveSourceProfile(compositionContext, templateSiteName) {
  const profiles = compositionContext?.sourceProfilesByDomain || {};
  const domain = normalizeDomain(templateSiteName);
  if (domain && profiles[domain]) return profiles[domain];
  // Single-profile contexts (the common compose case) apply to that
  // template's sections only — an unknown site gets no identity pairs.
  return null;
}

function clientAddressLine(address = {}) {
  return [address.lineOne, address.lineTwo, address.city, address.county, address.postcode]
    .map(clean)
    .filter(Boolean)
    .join(', ');
}

/** Refresh the persistent semantic profile from the editable Client Info. */
export function refreshCompositionContextFromClientInfo(state) {
  if (!state?.compositionContext?.clientProfile) return false;
  const client = normalizeClientInfo(state.clientInfo, state.projectName || 'Untitled');
  const current = state.compositionContext;
  const nextProfile = {
    ...current.clientProfile,
    businessName: client.name || state.projectName || '',
    phone: client.phoneFormatted || client.phone || '',
    email: client.email || '',
    address: clientAddressLine(client.address),
    mapLink: client.address?.mapLink || '',
    // Client Info currently exposes the common six networks; retain any
    // extracted platforms (for example TikTok/Vimeo) that are not editable on
    // that panel while overlaying every value the user can edit there.
    social: {
      ...(current.clientProfile.social || {}),
      ...(client.social || {})
    },
    copy: {
      ...(current.clientProfile.copy || {}),
      tagline: client.tagline || ''
    }
  };
  if (JSON.stringify(nextProfile) === JSON.stringify(current.clientProfile)) return false;
  state.compositionContext = {
    ...current,
    appliedAt: Date.now(),
    clientProfile: nextProfile
  };
  return true;
}

/**
 * Skin one section's data in place (html + css strings) using the project's
 * composition context. Synchronous; safe when context is null (no-op).
 *
 * @param {{html?: string, originalHtml?: string, css?: string,
 *          category?: string, catalogKey?: string, templateSiteName?: string}} data
 * @param {object|null} compositionContext  state.compositionContext
 * @param {{parseHTML?: (html: string) => Document|null}} [deps]
 * @returns {typeof data} the same object, mutated
 */
export function applyCompositionSkin(data, compositionContext, deps = {}) {
  if (!data || !compositionContext?.clientProfile) return data;
  const parseHTML = deps.parseHTML || defaultParseHTML;
  const clientProfile = compositionContext.clientProfile;
  const sourceProfile = resolveSourceProfile(compositionContext, data.templateSiteName);
  const sourceDomain = normalizeDomain(sourceProfile?.sourceDomain || '');
  const seed = clean(data.catalogKey) || clean(data.sectionId) || 'section';
  const nextPhoto = createHashedPhotoPicker(clientProfile, seed);

  const skinHtml = (html) => {
    if (!clean(html)) return html;
    const doc = parseHTML(`<div data-pb-comp-root>${html}</div>`);
    const root = doc?.querySelector?.('[data-pb-comp-root]');
    if (!root) return html;

    if (sourceProfile) {
      const { pairs } = makeTextPairs(sourceProfile, clientProfile);
      walkTextNodes(root, (node) => {
        node.nodeValue = replaceTextWithPairs(node.nodeValue || '', pairs);
      });
      for (const el of root.querySelectorAll('*')) {
        for (const attr of TEXT_ATTRIBUTES) {
          if (!el.hasAttribute(attr)) continue;
          el.setAttribute(attr, replaceTextWithPairs(el.getAttribute(attr) || '', pairs));
        }
      }
    }

    applyContentBindings(
      root,
      data.category || '',
      clientProfile,
      compositionContext.options || {}
    );
    applyLinkPolicy(root, {
      sourceProfile: sourceProfile || {},
      clientProfile,
      sourceDomain,
      pageMap: compositionContext.pageMap || [],
      options: compositionContext.options || {}
    });
    skinAssets(root, sourceDomain, sourceProfile, clientProfile, nextPhoto);

    return root.innerHTML;
  };

  data.html = skinHtml(data.html);
  if (typeof data.originalHtml === 'string' && data.originalHtml) {
    data.originalHtml = data.html;
  }
  data.css = skinCssUrls(data.css);
  return data;
}

/**
 * Re-run the composition skin across every section of a state (Resync).
 * Mutates in place; returns the number of sections whose html/css changed.
 */
export function resyncComposition(state, deps = {}) {
  const context = state?.compositionContext;
  if (!context?.clientProfile) return { sectionsChanged: 0 };

  let sectionsChanged = 0;
  const visit = (section) => {
    if (!section) return;
    const before = `${section.html}\u0000${section.css}`;
    applyCompositionSkin(section, context, deps);
    if (`${section.html}\u0000${section.css}` !== before) sectionsChanged += 1;
  };

  for (const page of state.pages || []) {
    for (const section of page.sections || []) visit(section);
  }
  for (const section of Object.values(state.sharedSections || {})) visit(section);
  return { sectionsChanged };
}
